import { useEffect, useState } from "react";
import { useLang } from "@/contexts/LangContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X, Megaphone } from "lucide-react";

export default function AnnouncementsPage() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);

  async function load() {
    const { data } = await api.get("/announcements");
    setItems(data);
  }
  useEffect(() => { load(); }, []);

  function openNew() { setEdit({ title: "", message: "", active: true, isNew: true }); }
  const openEdit = (a) => setEdit({ ...a });

  async function save() {
    try {
      const payload = { title: edit.title, message: edit.message, active: edit.active };
      if (edit.isNew) await api.post("/announcements", payload);
      else await api.put(`/announcements/${edit.id}`, payload);
      toast.success("Saved"); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  }

  async function del(a) {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    await api.delete(`/announcements/${a.id}`);
    toast.success("Deleted"); load();
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-end flex-wrap gap-3">
        <div>
          <span className="text-xs uppercase tracking-[0.3em] text-gold">— Promo</span>
          <h1 className="text-3xl md:text-4xl font-heading mt-2">{t("ann.title")}</h1>
        </div>
        <button onClick={openNew} data-testid="ann-add-btn" className="btn-gold">
          <Plus className="h-4 w-4" /> {t("common.add")}
        </button>
      </div>

      <div className="space-y-3">
        {items.length === 0 && <div className="text-foreground/40 text-center py-8">{t("common.empty")}</div>}
        {items.map((a) => (
          <div key={a.id} className="card-luxury p-5 flex items-start justify-between gap-4" data-testid={`ann-item-${a.id}`}>
            <div className="flex items-start gap-3">
              <div className={`h-10 w-10 rounded-full grid place-items-center ${a.active ? "bg-leaf/15 text-leaf" : "bg-charcoal-600 text-foreground/40"}`}>
                <Megaphone className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-heading text-lg">{a.title}</h3>
                <p className="text-foreground/70 mt-1">{a.message}</p>
                {!a.active && <span className="text-xs text-red-400 mt-2 inline-block">Inactive</span>}
              </div>
            </div>
            <div className="flex gap-1">
              <button onClick={() => openEdit(a)} data-testid={`ann-edit-${a.id}`} className="p-2 rounded-full hover:bg-gold/10 text-gold">
                <Edit2 className="h-4 w-4" />
              </button>
              <button onClick={() => del(a)} data-testid={`ann-delete-${a.id}`} className="p-2 rounded-full hover:bg-red-500/10 text-red-400">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {edit && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" onClick={() => setEdit(null)}>
          <div className="card-luxury p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-xl font-heading">{edit.isNew ? t("common.add") : t("common.edit")}</h2>
              <button onClick={() => setEdit(null)}><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-foreground/60 mb-1">{t("ann.titleField")}</label>
                <input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })}
                  className="w-full bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-foreground/60 mb-1">{t("ann.messageField")}</label>
                <textarea rows="4" value={edit.message} onChange={(e) => setEdit({ ...edit, message: e.target.value })}
                  className="w-full bg-charcoal-900 border border-charcoal-600 rounded-md px-3 py-2" />
              </div>
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} className="accent-leaf h-4 w-4" />
                {t("ann.active")}
              </label>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={() => setEdit(null)} className="btn-outline-gold flex-1 justify-center">{t("common.cancel")}</button>
              <button onClick={save} data-testid="ann-save-btn" className="btn-gold flex-1 justify-center">{t("common.save")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
